import { TypeAnimation } from 'react-type-animation'
import { createRef, useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { SourceArticles } from './SourceArticles'
import {
  getArticleTeaserData,
  ArticleData,
} from '@ta-interaktiv/react-article-teasers'
import Rating from '../Rating'
import { Fragment } from 'react'
import { marked } from "marked"

const formatMessage = (message: ChatInterface) => {
  const paragraphs = message.text.split('\n')

  return paragraphs.map((p, index) => (
    <p key={`message-${message.id}-paragraph-${index}`}>{p}</p>
  ))
}

interface ParsedElement {
  key: string
  type: string
  content: string | ParsedElement[]
}

const parseChildren = (
  element: Element,
  key: string,
  depth: number[] = []
): string | ParsedElement[] => {
  const children = Array.from(element.childNodes)

  // If there are no children, return the text content
  if (children.length === 0) {
    return element.textContent ? element.textContent.trim() : ''
  }

  // Map each child to its parsed representation
  const content = children
    .map((child, index): ParsedElement | null => {
      const newDepth = [...depth, index + 1]
      const uniqueKey = `${key}-chunk-${newDepth.join('-')}`

      // If the child is an element, continue to recursively parse it
      if (child.nodeType === Node.ELEMENT_NODE && child instanceof Element) {
        return {
          key: uniqueKey,
          type: child.tagName.toLowerCase(),
          content: parseChildren(child, key, newDepth),
        }
        // If the child is a text node, return its text content
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ? child.textContent.trim() : ''
        if (text === '') return null
        return {
          key: uniqueKey,
          type: 'text',
          content: text,
        }
      }
      return null
    })
    // Remove null and empty elements
    .filter(
      (item): item is ParsedElement => item !== null && item.content !== ''
    )

  return content
}

const parseHtml = (message: ChatInterface): ParsedElement[] => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(message.text, 'text/html')

  return parseChildren(doc.body, `message-${message.id}`) as ParsedElement[]
}

const DrawStringElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  const ref = createRef<HTMLSpanElement>()
  const [showCursor, setShowCursor] = useState<boolean>(true)

  return (
    <StyledTypeAnimation
      showCursor={showCursor}
      ref={ref}
      key={element.key}
      sequence={[
        element.content as string,
        () => {
          setShowCursor(false)
          handleElementDrawn()
        },
      ]}
      speed={85}
      cursor={false}
    />
  )
}

const DrawParsedElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  const [elementIndex, setElementIndex] = useState(1)
  const ElementType = element.type as keyof JSX.IntrinsicElements

  useEffect(() => {
    // Reset the index if the element type changes
    setElementIndex(1)
  }, [element.type])

  const h = () => {
    setElementIndex((prevIndex) => prevIndex + 1)
    if (elementIndex === (element.content as ParsedElement[]).length) {
      handleElementDrawn()
    }
  }

  return (
    <ElementType>
      {(element.content as ParsedElement[])
        .slice(0, elementIndex)
        .map((child, index) => (
          <Fragment key={index}>
            {child.type === 'text' ? (
              <DrawStringElement element={child} handleElementDrawn={h} />
            ) : (
              <DrawParsedElement element={child} handleElementDrawn={h} />
            )}
          </Fragment>
        ))}
    </ElementType>
  )
}

const DrawElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  return element.type === 'text' ? (
    <DrawStringElement
      element={element}
      handleElementDrawn={handleElementDrawn}
    />
  ) : (
    <DrawParsedElement
      element={element}
      handleElementDrawn={handleElementDrawn}
    />
  )
}

const Message = ({ message }: { message: ChatInterface }) => {
  const [showArticles, setShowArticles] = useState(false)
  const [elementIndex, setElementIndex] = useState(1)
  const [articles, setArticles] = useState(new Map())
  const theme = useTheme()
  const elements = parseHtml(message)

  const handleEndWriting = () => {
    setTimeout(() => {
      setShowArticles(true)
    }, 350)
  }

  useEffect(() => {
    if (message.articles && message.articles.length > 0) {
      message.articles.forEach((article) => {
        getArticleTeaserData(theme.tenant.key, article).then(
          (articleTeaserData: ArticleData) => {
            if (articleTeaserData) {
              setArticles(
                (articles) => new Map(articles.set(article, articleTeaserData))
              )
            }
          }
        )
      })
    }
  }, [message])

  const handleElementDrawn = () => {
    if (elementIndex === elements.length) {
      handleEndWriting()
    } else {
      setElementIndex((prevIndex) => prevIndex + 1)
    }
  }

  return (
    <Container type={message.type}>
      <TextWidth>
        {message.type === 'bot' ? (
          <>
            <Text>
              <>
                {elements.slice(0, elementIndex).map((element, index) => {
                  return (
                    <DrawElement
                      key={element.key}
                      element={element}
                      handleElementDrawn={handleElementDrawn}
                    />
                  )
                })}
              </>
            </Text>
            {showArticles && articles.size > 0 && (
              <SourceArticles articles={articles} />
            )}
            <RatingContainer>
              <Rating id={message.id} />
            </RatingContainer>
          </>
        ) : (
          formatMessage(message)
        )}
      </TextWidth>
    </Container>
  )
}

const cursorAnimation = `
  &::after {
    content: '';
    width: 1.15rem;
    height: 2rem;
    background-color: var(--text-color);
    position: relative;
    display: inline-block;
    animation: cursor 500ms infinite step-start;
    transition: opacity 0.5s ease-in-out;
    opacity: 0.65;
  }

  @keyframes cursor {
    50% {
      opacity: 0;
    }
  }
`

const Text = styled.div`
  // background-color: #f1f3f7;
  border-radius: 1rem;
  padding-top: 2rem;
  position: relative;
`

const Container = styled.div<{ type: 'bot' | 'user' }>`
  position: relative;
  background-color: ${({ type, theme }) =>
    type === 'bot' ? theme.colorGreyLight : theme.colorSiteBackground};
  border-color: ${({ theme }) => theme.colorGrey};
  border-style: solid;
  border-width: 1px 0 0 0;

  // &::after {
  //   content: '';
  //   position: absolute;
  //   top: 2rem;
  //   left: 2rem;
  //   width: 4rem;
  //   height: 4rem;
  //   border-radius: 0.5rem;
  //   background-color: ${({ theme }) => '#f1f3f7'};
  //   display: ${({ type }) => (type === 'bot' ? 'block' : 'none')};
  // }
`

const StyledTypeAnimation = styled(TypeAnimation)<{ showCursor: boolean }>`
  ${({ showCursor }) => showCursor && cursorAnimation}
`

const RatingContainer = styled.div`
  text-align: right;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

export default Message
